import {
  EUser,
  ESnapshotExists,
  ENotice,
  EOrganization,
  EInvoice,
  exists,
  ESnapshot
} from 'lib/types';
import { getDisplayName } from 'lib/helpers';
import { State, BillingStatusType, InvoiceStatus } from 'lib/enums';
import { getUserStripeId } from 'lib/utils/users';
import { createStripeCustomer } from 'api/stripe';

// Customer facing errors that are displayed in a error toast when invoice creation fails
export const INVOICE_CREATION_IN_PROGRESS_ERROR =
  'Invoice creation is already in progress';
export const NOTICE_HAS_INVOICE_ERROR =
  'The notice already has an active invoice';

const mapEnumToState = (stateEnum: number): string | undefined => {
  const item = State.items().find(item => item.value === stateEnum);
  return item?.label;
};

export type StripeCustomerAddress = {
  line1: string;
  city: string;
  state: string;
  postal_code: string;
};

const getAddressObj = (advertiser: EUser): StripeCustomerAddress | {} => {
  if (!advertiser.address) return {};
  return {
    line1: advertiser.address,
    city: advertiser.city,
    state:
      typeof advertiser.state === 'number'
        ? mapEnumToState(advertiser.state)
        : undefined,
    postal_code: advertiser.zipCode
  };
};

const createUserStripeId = async (
  advertiserSnap: ESnapshotExists<EUser>
): Promise<string> => {
  const advertiser = advertiserSnap.data();

  const [error, result] = await createStripeCustomer(
    advertiser.organizationName ||
      (advertiser.name
        ? advertiser.name
        : getDisplayName(advertiser.firstName, advertiser.lastName)),
    advertiser.email,
    advertiserSnap.id,
    getAddressObj(advertiser)
  );

  if (error) {
    throw error;
  }

  return result;
};

/**
 * Returns with the following preference:
 * - The organization's Stripe ID, if the user is a member of it.
 * - The user's Stripe ID.
 * - The Stripe ID of a new customer (which will only be added to the user if it is an individual)
 */
export const getOrCreateUserStripeId = async (
  userSnap: ESnapshotExists<EUser>,
  organizationSnap: ESnapshot<EOrganization> | undefined
): Promise<string> => {
  const stripeId = await getUserStripeId(userSnap, organizationSnap);
  if (!stripeId) {
    return await createUserStripeId(userSnap);
  }

  return stripeId;
};

const getStatusEnum = (
  userNoticeSnap: ESnapshotExists<ENotice>,
  invoiceSnap: ESnapshot<EInvoice> | undefined,
  isPublisher: boolean,
  isSubscriptionBilled: boolean
) => {
  const { transfer } = userNoticeSnap.data();
  if (!exists(invoiceSnap)) {
    return BillingStatusType.invoice_not_submitted;
  }

  if (!isPublisher && isSubscriptionBilled) {
    return BillingStatusType.publisher_paid;
  }

  const isInvoicedOutsideColumn = invoiceSnap.data().invoiceOutsideColumn;

  if (invoiceSnap && !transfer) {
    if (isInvoicedOutsideColumn)
      return BillingStatusType.invoiced_outside_column;
    if ([InvoiceStatus.unpaid.value].includes(invoiceSnap.data().status))
      return BillingStatusType.invoice_submitted_to_advertiser;
    if (
      [
        InvoiceStatus.paid.value,
        InvoiceStatus.partially_refunded.value
      ].includes(invoiceSnap.data().status)
    )
      return BillingStatusType.invoice_paid_by_advertiser;
    if (invoiceSnap.data().status === InvoiceStatus.initiated.value)
      return BillingStatusType.payment_initiated;
    if (invoiceSnap.data().status === InvoiceStatus.payment_failed.value)
      return BillingStatusType.payment_failed;
  }

  if (invoiceSnap.data().status === InvoiceStatus.refunded.value)
    return BillingStatusType.payment_refunded;

  if (!isPublisher && transfer) {
    if (isInvoicedOutsideColumn)
      return BillingStatusType.invoiced_outside_column;
    return BillingStatusType.invoice_paid_by_advertiser;
  }

  if (isPublisher && transfer) {
    if (isInvoicedOutsideColumn)
      return BillingStatusType.invoiced_outside_column;
    return BillingStatusType.transfer_created;
  }
  return BillingStatusType.invoice_not_submitted;
};

export const getOrCreateAdvertiserStripeId = async (
  noticeSnap: ESnapshotExists<ENotice>,
  advertiserSnap: ESnapshotExists<EUser>
) => {
  // We prefer the stripe ID on the notice filedBy organization but fall
  // back onto the user.
  const filedByOrganization = await noticeSnap.data().filedBy?.get();
  let stripeId = filedByOrganization?.data()?.stripeId;
  if (!stripeId) {
    stripeId = await getOrCreateUserStripeId(
      advertiserSnap,
      filedByOrganization
    );
  }
  return stripeId;
};

const BillingFunctions = {
  getStatusEnum
};

export default BillingFunctions;
